/**
 * 絵コンテモードの本編シーンの「モーショングラフィックス」カード (docs/features/brand-cut-motion-graphics-plan.md §10.13 4. / 5. / 6. / 8.・§10.13.1)。
 *
 * - シーンの 3 点メニュー「モーションで上書き」(SceneItem) で `video_input_mode='motion'` になったシーンだけに出る。シナリオ生成時に
 *   AI がモーション向きと判定したシーンは backend が初期値 'motion' を書くので最初から出る。× で null に戻し (絵コンテ動画のまま)
 *   カードは消える (useStoryboardMotionMode)
 * - カードは区間 (`GET .../storyboard/spans`)・「境界を調整」・「モーションを生成」(100pt 定額)・結果のサムネイル・
 *   「利用しない」(トグル)・効果音スイッチ (既定オフ)・「テロップを表示」(既定オフ = 上書き区間の絵コンテのテロップを隠す)・
 *   サムネイル下の履歴 (過去に生成したモーションから選び直す。VideoHistorySelector の kind='motion_overlay') を持つ。
 *   鮮度 (stale / trim / freeze / boundary_stale) の注記もここ
 * - 区間の算出と鮮度の正本は backend。ここは spans の応答を読むだけ
 */
import { lazy, Suspense, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Tooltip } from '../../../common/Tooltip';
import { ToggleSwitch } from '../../../common/ToggleSwitch';
import { MotionModelSelect } from '../../common/MotionModelSelect';
import { useToast } from '../../../../hooks/useToast';
import { getScenario, updateSceneMotionOverlay } from '../../../../features/scenarios/api';
import { parseMotionOverlay } from '../../../../features/scenarios/storyboardSpans';
import type { Scenario } from '../types';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { useSceneStateContext } from '../hooks/SceneStateContext';
import { invalidateStoryboardSpans, setStoryboardSpans, useStoryboardSpans } from '../hooks/useStoryboardSpans';
import { storyboardMotionOverlayKey } from '../hooks/useVideoHandlers';
import { MotionVideoThumb } from './SceneFrameCarousel';
import { VideoHistorySelector } from '../VideoHistorySelector';
import { useStoryboardMotionMode } from '../hooks/useStoryboardMotionMode';
import {
  boundaryStaleNotice,
  findSceneSpan,
  motionGenerateDisabledReason,
  motionOverlayFreshnessNotice,
  spanDurationSec,
  spanLabelParts,
  storyboardCardNumber,
} from './storyboardMotionLogic';
import styles from '../ScenarioGenerator.module.css';
import '../../../../styles/buttons.css';

const StoryboardBoundaryModal = lazy(() => import('./StoryboardBoundaryModal').then((m) => ({ default: m.StoryboardBoundaryModal })));

export interface StoryboardMotionCardProps {
  scenario: Scenario;
  scene: Scenario['scenes'][number];
  sceneKey: string;
  /** モーションの定額 (SceneItem の sceneCredits = credit_rate_config の motion-graphics-scene)。0 = 未設定 */
  sceneCredits: number;
}

export function StoryboardMotionCard({ scenario, scene, sceneKey: key, sceneCredits }: StoryboardMotionCardProps) {
  const {
    setLastResult,
    handleGenerateStoryboardMotionOverlay,
    creditsData,
    isStorageExceeded,
    openPurchaseModal,
  } = useSceneHandlers();
  const { sceneVideoLoading, sceneVideoError, sceneVideoMaterialId } = useSceneStateContext();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { pending, setMode } = useStoryboardMotionMode(scene, key);
  const overlayKey = storyboardMotionOverlayKey(scenario.id, scene.order);
  const loading = !!sceneVideoLoading[overlayKey];

  // 保存済みの上書き (disabled でも残る。spans 応答は disabled を含まないのでこちらから読む)
  const savedOverlay = parseMotionOverlay(scene.generation_metadata);
  // 効果音スイッチの表示値: 保存済みの上書きがあればその値、無ければローカルの選択 (既定オフ)
  const [mixSoundDraft, setMixSoundDraft] = useState(false);
  const mixSound = savedOverlay ? savedOverlay.mix_sound : mixSoundDraft;
  // 「テロップを表示」: 既定オフ (上書き区間ではモーション側が画面に見出し・キーワードを描くので、絵コンテのテロップは隠す)
  const [showTelopDraft, setShowTelopDraft] = useState(false);
  const showTelop = savedOverlay ? savedOverlay.show_telop : showTelopDraft;
  const [overlayUpdating, setOverlayUpdating] = useState(false);
  // 「利用しない」が ON (元の映像のまま)。トグル以外をグレーアウトする
  const overlayOff = !!savedOverlay?.disabled;
  const [boundaryOpen, setBoundaryOpen] = useState(false);

  // spans はモーションのシーンがあるときだけ取る (このシーンが motion でなくても、同じシナリオの別シーンの取得と共有される)
  const anyMotion = (scenario.scenes || []).some((s) => !s.brand_cut_position && s.video_input_mode === 'motion');
  const spans = useStoryboardSpans(scenario.id, scenario.scenes || [], { enabled: anyMotion });
  const lookup = findSceneSpan(spans.data, scene.id);
  const cardNumber = lookup ? storyboardCardNumber(scenario.scenes || [], lookup.card.card_scene_id, sceneVideoMaterialId, scenario.id) : null;

  /** 対象シーン (と隣接シーン) の generation_metadata をサーバーから取り直してローカル state に写す
   *  (span_override / motion_overlay が変わると useStoryboardSpans の要約が変わり spans が取り直される)。 */
  const refreshSceneMetadata = async () => {
    try {
      const updated = await getScenario(scenario.id);
      const metaById = new Map((updated.scenes || []).map((sc) => [sc.id as string, sc.generation_metadata as Record<string, unknown> | undefined]));
      setLastResult((prev) => (prev ? {
        ...prev,
        scenarios: prev.scenarios.map((s: any) => (s.id !== scenario.id ? s : {
          ...s,
          scenes: s.scenes.map((sc: any) => (metaById.has(sc.id) ? { ...sc, generation_metadata: metaById.get(sc.id) } : sc)),
        })),
      } : prev));
    } catch (err) {
      console.warn('[storyboard-motion] シーンの再取得に失敗しました', err);
    }
  };

  const handleOverlayUpdate = async (payload: { disabled?: boolean; mix_sound?: boolean; show_telop?: boolean; material_id?: string }) => {
    setOverlayUpdating(true);
    try {
      // 応答は {scene_id, motion_overlay} (spans の形ではない) なので、シーンの metadata を取り直して spans を無効化する
      await updateSceneMotionOverlay(scene.id, payload);
      await refreshSceneMetadata();
      await invalidateStoryboardSpans(queryClient, scenario.id);
      if (payload.material_id) toast.success('モーションを切り替えました');
    } catch {
      toast.error(payload.material_id ? 'モーションの切り替えに失敗しました' : '更新に失敗しました');
    } finally {
      setOverlayUpdating(false);
    }
  };

  const handleMixSoundChange = (checked: boolean) => {
    if (savedOverlay) {
      void handleOverlayUpdate({ mix_sound: checked });
    } else {
      setMixSoundDraft(checked);
    }
  };

  const handleShowTelopChange = (checked: boolean) => {
    if (savedOverlay) {
      void handleOverlayUpdate({ show_telop: checked });
    } else {
      setShowTelopDraft(checked);
    }
  };

  const hasSpan = !!lookup;
  const spanSec = lookup ? spanDurationSec(lookup.span) : 0;
  const disabledReason = motionGenerateDisabledReason({
    hasSpan, spanSec, rateMissing: sceneCredits <= 0, loading,
  });
  const totalAvailable = creditsData?.total_available ?? creditsData?.credits_balance ?? 0;
  const creditInsufficient = !!creditsData && sceneCredits > 0 && totalAvailable < sceneCredits;
  const freshness = motionOverlayFreshnessNotice(lookup?.overlay);
  const boundaryNotice = boundaryStaleNotice(lookup?.span);
  const generated = !!savedOverlay;

  const handleGenerate = () => {
    if (!handleGenerateStoryboardMotionOverlay || disabledReason) return;
    if (creditInsufficient) {
      openPurchaseModal();
      return;
    }
    void handleGenerateStoryboardMotionOverlay(scenario, scene, { mixSound, showTelop });
  };

  return (
    <div className={styles.flowStepSection} style={{ position: 'relative' }} data-testid={`storyboard-motion-card-${scene.order}`}>
      {/* × = 絵コンテ動画に戻す (video_input_mode を null に。生成結果は metadata に残り、再び選べば戻る)。カードの右上の隅に乗せる */}
      <Tooltip content="絵コンテ動画に戻す">
        <button
          type="button"
          className={styles.storyboardMotionClose}
          aria-label="絵コンテ動画に戻す"
          disabled={pending || loading}
          onClick={(e) => { e.stopPropagation(); void setMode('storyboard'); }}
          data-testid="storyboard-motion-close"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </Tooltip>
      <div className={styles.stepHeader} style={{ cursor: 'default', gap: 8 }}>
        <div className={styles.stepTitle} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: overlayOff ? 0.4 : 1 }}>
          <img src="/scene_motion.svg" alt="" style={{ width: 14, height: 14 }} />
          <span style={{ whiteSpace: 'nowrap' }}>モーショングラフィックス</span>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }} onClick={(e) => e.stopPropagation()}>
          {/* 利用しない (元の映像のまま): ヘッダー右端のトグル。グレーアウトの対象外 */}
          {savedOverlay && (
            <div data-testid="storyboard-motion-disabled">
              <ToggleSwitch
                checked={savedOverlay.disabled}
                onChange={(checked) => { void handleOverlayUpdate({ disabled: checked }); }}
                label="利用しない"
                disabled={overlayUpdating || loading}
              />
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }} onClick={(e) => e.stopPropagation()}>
        {/* 「利用しない」のときはトグル以外を薄くして操作も止める */}
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: overlayOff ? 0.4 : 1, pointerEvents: overlayOff ? 'none' : undefined }}
          aria-disabled={overlayOff || undefined}
          data-testid="storyboard-motion-body"
        >
          {/* 区間 */}
          <div className={styles.frameLinkModeLabel} data-testid="storyboard-motion-span" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {lookup
              ? (() => {
                const parts = spanLabelParts(cardNumber, lookup.span);
                return (
                  <>
                    {/* 担当カード = 背景色付きのチップ・開始-終了はシーンカードの開始・終了秒と同じタイムコードの書体 */}
                    {parts.card && <span className={styles.storyboardMotionCardChip}>{parts.card}</span>}
                    <span>
                      <span className={styles.storyboardMotionTimecode}>{parts.range}</span>
                      ・{parts.duration}
                    </span>
                  </>
                );
              })()
              : (spans.isLoading && spans.hasCardVideo ? '区間を読み込み中…' : '絵コンテ動画を生成すると区間が決まります')}
          </div>
          {boundaryNotice && (
            <div className={styles.motionSyncStaleNotice} style={{ marginTop: 0 }}>{boundaryNotice}</div>
          )}
          {freshness && (
            <div
              className={freshness.tone === 'stale' ? styles.motionSyncStaleNotice : styles.frameLinkModeLabel}
              style={{ marginTop: 0 }}
              data-testid={`storyboard-motion-freshness-${freshness.tone}`}
            >
              {freshness.text}
            </div>
          )}

          {/* 結果のサムネイル + 操作 */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            {savedOverlay && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, flexShrink: 0 }}>
                <div
                  style={{
                    width: 96, height: 54, borderRadius: 4, overflow: 'hidden',
                    background: 'rgba(0, 0, 0, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: savedOverlay.disabled ? 0.4 : 1,
                  }}
                  title={savedOverlay.disabled ? '利用しない (元の映像のまま)' : '生成したモーション'}
                >
                  <MotionVideoThumb materialId={savedOverlay.material_id} />
                </div>
                {/* 過去に生成したモーションから選び直す (モデル名付き)。選ぶと「利用しない」は解除される */}
                <div data-testid="storyboard-motion-history">
                  <VideoHistorySelector
                    kind="motion_overlay"
                    sceneId={scene.id}
                    currentMaterialId={savedOverlay.material_id}
                    disabled={overlayUpdating || loading}
                    onSelect={(materialId) => {
                      if (materialId === savedOverlay.material_id) return;
                      void handleOverlayUpdate({ material_id: materialId });
                    }}
                  />
                </div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <Tooltip content={lookup ? '対象シーンの開始・終了の位置をフレーム単位で決めます' : '絵コンテ動画を生成すると調整できます'}>
                  {/* 枠線・塗りなしのテキストボタン (ハサミ付き) */}
                  <button
                    type="button"
                    disabled={!lookup || loading}
                    onClick={() => setBoundaryOpen(true)}
                    data-testid="storyboard-motion-boundary"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0,
                      background: 'none', border: 'none', color: 'rgba(245, 241, 230, 0.75)', fontSize: '0.8rem',
                      cursor: (!lookup || loading) ? 'default' : 'pointer', opacity: (!lookup || loading) ? 0.45 : 1,
                    }}
                  >
                    <img src="/scissors_2.svg" alt="" style={{ width: 14, height: 14 }} />
                    境界を調整
                  </button>
                </Tooltip>
                {handleGenerateStoryboardMotionOverlay && (
                  <Tooltip content={disabledReason ?? `${sceneCredits}ポイントを消費します`}>
                    <div className={`${styles.videoGenerateGroup} ${loading ? 'ai-loading-active' : ''}`} style={{ marginLeft: 'auto' }}>
                      <button
                        type="button"
                        className={styles.sceneVideoButton}
                        style={{ borderRadius: 999, paddingRight: 12 }}
                        disabled={!!disabledReason || isStorageExceeded || pending}
                        onClick={handleGenerate}
                        data-testid="storyboard-motion-generate"
                      >
                        {loading ? 'モーション生成中…' : (
                          <>
                            {generated || freshness?.tone === 'stale' ? 'モーションを再生成' : 'モーションを生成'}
                            <span className={styles.creditBadge}>{sceneCredits}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </Tooltip>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                {handleGenerateStoryboardMotionOverlay && (
                  <MotionModelSelect compact disabled={loading || pending} testId={`storyboard-motion-model-${scene.order}`} />
                )}
                <Tooltip content="オンにすると、モーションに付く効果音をこの区間の元の音声 (セリフ・環境音) に重ねます。オフのときはモーションは映像だけで、音は絵コンテ動画のままです。生成後も切り替えられます">
                  <label className={styles.frameLinkModeLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: overlayUpdating ? 'default' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={mixSound}
                      disabled={overlayUpdating || loading}
                      onChange={(e) => handleMixSoundChange(e.target.checked)}
                      data-testid="storyboard-motion-mix-sound"
                    />
                    モーションの効果音
                  </label>
                </Tooltip>
                <Tooltip content="オンにすると、この区間に絵コンテのテロップも重ねます。オフのときはモーション側の画面の文字と重ならないよう、動画エディタと書き出しで隠します (動画エディタで戻せます)">
                  <label className={styles.frameLinkModeLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: overlayUpdating ? 'default' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showTelop}
                      disabled={overlayUpdating || loading}
                      onChange={(e) => handleShowTelopChange(e.target.checked)}
                      data-testid="storyboard-motion-show-telop"
                    />
                    テロップを表示
                  </label>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

          {sceneVideoError[overlayKey] && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: 'rgba(220, 38, 38, 0.1)',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              borderRadius: '4px',
              color: '#f87171',
              fontSize: '0.875rem',
              lineHeight: '1.5',
            }}>
              {sceneVideoError[overlayKey]}
            </div>
          )}
      </div>

      {boundaryOpen && lookup && (
        <Suspense fallback={null}>
          <StoryboardBoundaryModal
            scenarioId={scenario.id}
            sceneId={scene.id}
            sceneOrder={scene.order}
            card={lookup.card}
            spanIndex={lookup.spanIndex}
            sceneOrderById={Object.fromEntries((scenario.scenes || []).map((s) => [s.id, s.order]))}
            onClose={() => setBoundaryOpen(false)}
            onSaved={async (res) => {
              setStoryboardSpans(queryClient, scenario.id, spans.signature, res);
              setBoundaryOpen(false);
              await refreshSceneMetadata();
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
